import { PlanOrganizationSelection } from './PlanOrganizationSelection'
import { AnnualPlanRole } from '../../../../models/AnnualPlanRole'
import { useEffect, useState } from 'react'
import { useAuth } from '../../../../hooks/use-auth'
import {
  GetAnnualPlanAndOrgName,
  GetAnnualPlanTeam,
  GetPlanOrganizationsForAnnualPlan
} from '../../../../services/AnnualPlanService'
import { generatePath } from 'react-router'
import { AppRoutes } from '../../../Routes'
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'
import { toast } from 'react-toastify'
import { PlanningPriorityActivitiesPlanOrganization } from './PlanningPriorityActivitiesPlanOrganization'
import { PlanningPriorityActivitiesPlanOrganizationPriority } from './PlanningPriorityActivitiesPlanOrganizationPriority'
import { CircularProgress, Typography } from '@mui/material'
import { AnnualPlanTabs } from '../PlanningDetails'

interface Props {
  annualPlanId: number
  rcnId: number
  onUpdateStatus: () => void
  implementationPhase?: boolean
}

export const PriorityActivitiesTab = (props: Props): JSX.Element => {
  const { planOrgId } = useParams()
  const [searchParams] = useSearchParams()
  const nav = useNavigate()
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void checkTeamAccess()
  }, [props.annualPlanId, user, planOrgId])

  const checkTeamAccess = async (): Promise<void> => {
    if (user == null) return

    try {
      const planMembers = await GetAnnualPlanTeam(props.annualPlanId)
      const member = planMembers.find(member => member.userId === user.id)

      if (member == null) {
        setError('User is not a member of this plan')
        return
      }

      const userRole = member.annualPlanRole as AnnualPlanRole
      const reviewOthers = searchParams.get('reviewOthers') === 'true'

      if (userRole === AnnualPlanRole.PlanContributor && !reviewOthers && planOrgId == null) {
        void navigateToPlanOrganization()
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const navigateToPlanOrganization = async (): Promise<void> => {
    if (user?.currentOrgId == null) return

    const { success, response: planOrgs } = await GetPlanOrganizationsForAnnualPlan(props.annualPlanId)
    if (!success) {
      toast.error('Failed to fetch Plan Organizations for Annual Plan.')
      return
    }

    const { organizationName } = await GetAnnualPlanAndOrgName(
      props.annualPlanId.toString(),
      user?.currentOrgId.toString()
    )

    const selectedIsdPlanOrg = planOrgs.find((po) => po.name === organizationName)

    const route = generatePath(
      props.implementationPhase === true
        ? AppRoutes.IMPLEMENTATION_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION
        : AppRoutes.PLANNING_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION,
      {
        rcnId: props.rcnId.toString(),
        planId: props.annualPlanId.toString(),
        tab: String(AnnualPlanTabs.PriorityActivities),
        planOrgId: selectedIsdPlanOrg?.id.toString() ?? null
      }
    )
    nav(route)
  }

  if (isLoading) {
    return <CircularProgress />
  }

  if (error != null) {
    return <Typography variant='body1' textAlign='center'>{error}</Typography>
  }

  return <>
    <Routes>
      <Route index
        element={<PlanOrganizationSelection
          annualPlanId={props.annualPlanId}
          rcnId={props.rcnId}
          implementationPhase={props.implementationPhase}
        />}
      />

      <Route
        path={AppRoutes.PLANNING_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION.slice(AppRoutes.PLANNING_DETAILS.length)}
        element={<PlanningPriorityActivitiesPlanOrganization />}
      />

      <Route
        path={AppRoutes.PLANNING_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION_PRIORITY.slice(AppRoutes.PLANNING_DETAILS.length)}
        element={<PlanningPriorityActivitiesPlanOrganizationPriority onUpdateStatus={props.onUpdateStatus} />}
      />

      <Route
        path={AppRoutes.IMPLEMENTATION_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION.slice(AppRoutes.IMPLEMENTATION_DETAILS.length)}
        element={<PlanningPriorityActivitiesPlanOrganization />}
      />

      <Route
        path={AppRoutes.IMPLEMENTATION_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION_PRIORITY.slice(AppRoutes.IMPLEMENTATION_DETAILS.length)}
        element={<PlanningPriorityActivitiesPlanOrganizationPriority onUpdateStatus={props.onUpdateStatus} />}
      />
    </Routes>
  </>
}
